/**
 * The bedroom's floor props: the rug under the bed and the olive tree in a
 * corner.
 */

const { footprintFor } = require('../../../geometry/objectFootprint');
const {
  doPolygonsOverlap,
} = require('../../../bathroom-analyzer/pipeline/geometry');
const {
  isPointInsideOrOnPolygon,
  isPointInsidePolygon,
} = require('../../../geometry/pointInPolygon');
const { objectsYOverlap } = require('../../../geometry/verticalOverlap');
const {
  objectWorldFromLocal,
  isFloorPointClear,
} = require('../../../dorm-styling/plan/shared');
const {
  getFloorPolygon,
  getFloorY,
} = require('../../../dorm-styling/dormDecorGeometry');
const IN = 0.0254;
/** How finely a rug is slid out from a wall, looking for floor. */
const RUG_SHIFT_STEP_M = 2 * IN;
const { rugCandidatesForBed, propBox } = require('../../bed/dressBed');
const { doorSwingZones, clearsDoorSwings } = require('../doorSwing');
const { FLOOR_CLEARANCE_M } = require('../constants');
const { bedLocal } = require('./bedHost');
const {
  BED_WALKWAY_M,
  RUG_FLOOR_SLACK_M,
  FLOOR_PLANT_WALL_GAP_M,
} = require('./constants');

/**
 * The rug, laid crosswise under the bed: the bed's own size first, then each
 * smaller one. A size is taken only when every corner lies on the floor and it
 * stays out of every door's swing (a rug over 0.4 in catches under a door
 * whose bottom clears the floor by 0.5-0.75 in, and a tight modern seal by as
 * little as 3/16 in — vmathome.com, "low-profile rugs for entryways"; Megan,
 * 2026-10-28: avoid it) — a rug a door drags across, or one running
 * through a doorway, is the thing a stager would never lay. A room that takes
 * none gets none.
 *
 * @returns {{ fields: object|null, variant: string|null, shrunk: boolean,
 *   shifted: boolean, reason: string|null }}
 */
function placeRug(room, bed, size, footZ) {
  const floor = getFloorPolygon(room);
  if (!floor?.length) return { fields: null, reason: 'no-floor-outline' };
  const ry = bed.rotation?.y || 0;
  const keepouts = doorSwingZones(room);
  // The bed's own size first; within a size, the source's 22 in past the
  // foot, then down its range to 18 in before giving the size up.
  const candidates = [22, 20, 18].flatMap((past) =>
    rugCandidatesForBed(size, footZ, past * IN)
  );
  candidates.sort((p, q) => q.dims.x * q.dims.z - p.dims.x * p.dims.z);
  const ownArea = candidates[0].dims.x * candidates[0].dims.z;
  let reason = null;
  for (const c of candidates) {
    // Centred on the bed first. A bed pushed into a corner (a twin, mostly)
    // leaves no floor for a centred rug on its wall side, and a stager slides
    // the rug out into the room rather than giving it up — never so far that
    // it stops running under the bed's whole width.
    const reach = Math.max(0, (c.dims.x - bed.dimensions.x) / 2);
    const shifts = [0];
    for (let d = RUG_SHIFT_STEP_M; d <= reach + 1e-9; d += RUG_SHIFT_STEP_M) {
      shifts.push(d, -d);
    }
    for (const dx of shifts) {
      const at = objectWorldFromLocal(bed, dx, c.z);
      const f = footprintFor(at, c.dims.x, c.dims.z, ry);
      if (!f) continue;
      if (
        !f.every((p) => isPointInsideOrOnPolygon(p, floor, RUG_FLOOR_SLACK_M))
      ) {
        reason = reason || 'rug-off-the-floor';
        continue;
      }
      if (keepouts.some((k) => doPolygonsOverlap(f, k))) {
        reason = 'rug-in-a-door-swing';
        continue;
      }
      return {
        fields: {
          center: { x: at.x, y: getFloorY(room) + c.dims.y / 2, z: at.z },
          dimensions: c.dims,
          rotation: { x: 0, y: ry, z: 0 },
        },
        variant: c.variant,
        shrunk: c.dims.x * c.dims.z < ownArea,
        shifted: dx !== 0,
        reason: null,
      };
    }
  }
  return { fields: null, reason };
}

/**
 * Is a box (centre + half-size, in plan) clear of the walkway round the bed —
 * 24 in beside each long side and across the foot — measured in the bed's
 * frame? The head end is exempt from the head of the bed to `beside`: that is
 * where the nightstands stand, and a tree beside a nightstand against the
 * head wall is the composition the research doc draws.
 */
function clearOfBedWalkway(bed, point, half, beside) {
  const p = bedLocal(bed, point);
  const hw = bed.dimensions.x / 2 + BED_WALKWAY_M;
  const z0 = -bed.dimensions.z / 2 + beside;
  const z1 = bed.dimensions.z / 2 + BED_WALKWAY_M;
  const r = half * Math.SQRT2; // the box turned to any angle
  return p.x + r < -hw || p.x - r > hw || p.z + r < z0 || p.z - r > z1;
}

/**
 * The point `d` in from both walls of a CONVEX floor corner, along its
 * bisector — exact at any angle, where an axis-aligned inset is right only in
 * a room drawn square to the axes. Null for a reflex corner (the inside of an
 * L), which is not a corner a tree stands in.
 */
function cornerInset(poly, i, d) {
  const n = poly.length;
  const c = poly[i];
  const a = poly[(i + n - 1) % n];
  const b = poly[(i + 1) % n];
  const u = (p) => {
    const l = Math.hypot(p.x - c.x, p.z - c.z);
    return l > 0 ? { x: (p.x - c.x) / l, z: (p.z - c.z) / l } : null;
  };
  const ea = u(a);
  const eb = u(b);
  if (!ea || !eb) return null;
  const bis = { x: ea.x + eb.x, z: ea.z + eb.z };
  const bl = Math.hypot(bis.x, bis.z);
  if (bl < 1e-6) return null;
  const half =
    Math.acos(Math.max(-1, Math.min(1, ea.x * eb.x + ea.z * eb.z))) / 2;
  const p = {
    x: c.x + (bis.x / bl) * (d / Math.sin(half)),
    z: c.z + (bis.z / bl) * (d / Math.sin(half)),
  };
  if (!isPointInsidePolygon(p, poly)) return null;
  // Square to the corner: the yaw that puts the box's local +x along one of
  // its walls (the stored-rotation convention, +x at (cos, -sin)).
  return { ...p, rotationY: Math.atan2(-ea.z, ea.x) };
}

/**
 * The olive tree: an empty corner, out of every door's swing and the bed's
 * walkway, nearest the bed's head first — the corner beside a nightstand.
 * Optional: a room with no such corner gets none.
 *
 * Not `planFloorCornerItems`: that refuses every corner of a wall carrying a
 * door, which on the corpus threw away most of the corners that passed every
 * other test, while the swing keep-out here already says exactly where the
 * door needs its floor.
 *
 * @param {object[]} planned  props already placed this run (the rug is flat
 *   and ignored; anything else is an obstacle)
 * @param {number} beside  how far from the head the nightstands reach
 */
function placeFloorPlant(room, bed, planned, beside) {
  const dims = propBox('staging-floor-plant');
  const half = Math.max(dims.x, dims.z) / 2;
  const floor = getFloorPolygon(room);
  if (!floor?.length) return null;
  const floorY = getFloorY(room);
  const band = {
    center: { y: floorY + dims.y / 2 },
    dimensions: { y: dims.y },
  };
  const obstacles = [
    ...(room.objects || []),
    ...planned.filter((e) => e.type !== 'staging-rug'),
  ].filter((o) => objectsYOverlap(band, o, { missingHeight: 'full' }));
  const keepouts = doorSwingZones(room);
  const head = objectWorldFromLocal(bed, 0, -bed.dimensions.z / 2);
  const points = floor
    .map((_, i) => cornerInset(floor, i, half + FLOOR_PLANT_WALL_GAP_M))
    .filter(Boolean)
    .sort(
      (p, q) =>
        Math.hypot(p.x - head.x, p.z - head.z) -
        Math.hypot(q.x - head.x, q.z - head.z)
    );
  const point = points.find(
    (p) =>
      isFloorPointClear(p, obstacles, half + FLOOR_CLEARANCE_M) &&
      clearsDoorSwings(p, keepouts, half) &&
      clearOfBedWalkway(bed, p, half, beside) &&
      (footprintFor(p, dims.x, dims.z, p.rotationY) || []).every((c) =>
        isPointInsideOrOnPolygon(c, floor, RUG_FLOOR_SLACK_M)
      )
  );
  if (!point) return null;
  return {
    center: { x: point.x, y: floorY + dims.y / 2, z: point.z },
    dimensions: dims,
    rotation: { x: 0, y: point.rotationY, z: 0 },
  };
}

module.exports = { placeRug, placeFloorPlant, clearOfBedWalkway, cornerInset };
